/**
 * JobTable Component
 * Premium table displaying jobs with Lucide icons and proper styling
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Job, JobStatus } from "../../types";
import { Button, Badge, EmptyState } from "../common";
import { formatDate, formatJobType, downloadFile } from "../../utils";
import { ROUTES } from "../../services/routes";
import {
  Eye,
  Download,
  Trash2,
  Hash,
  Target,
  Clock,
  Star,
  Zap,
  Link2,
  Users,
  Car,
  ParkingCircle,
  ArrowLeftRight,
  CircleDot,
  Utensils,
  BedDouble,
  Building2,
  PawPrint,
  BarChart3,
  Video,
  FileSearch,
  HardHat,
  Box,
  MonitorPlay,
} from "lucide-react";

// Job type to icon mapping
const JOB_TYPE_ICONS: Record<string, React.ReactNode> = {
  people_counting: <Users className="w-4 h-4" />,
  number_plate_detection: <Car className="w-4 h-4" />,
  parking_analysis: <ParkingCircle className="w-4 h-4" />,
  in_out_counting: <ArrowLeftRight className="w-4 h-4" />,
  pothole_detection: <CircleDot className="w-4 h-4" />,
  food_waste_estimation: <Utensils className="w-4 h-4" />,
  room_readiness: <BedDouble className="w-4 h-4" />,
  lobby_crowd_detection: <Building2 className="w-4 h-4" />,
  wildlife_detection: <PawPrint className="w-4 h-4" />,
  ppe_detection: <HardHat className="w-4 h-4" />,
  damage_detection_3d: <Box className="w-4 h-4" />,
  multi_camera: <MonitorPlay className="w-4 h-4" />,
};

interface JobTableProps {
  jobs: Job[];
  loading?: boolean;
  onDelete?: (jobId: string) => void;
  onRefresh?: () => void;
}

export const JobTable: React.FC<JobTableProps> = ({
  jobs,
  loading,
  onDelete,
  onRefresh,
}) => {
  const navigate = useNavigate();

  const handleViewJob = (jobId: string) => {
    navigate(ROUTES.JOB_DETAILS(jobId));
  };

  const handleDownload = (job: Job) => {
    if (!job?.annotated_output_url) return;
    try {
      const isVideo = job.input_file_path?.match(/\.(mp4|avi|mov|webm)$/i);
      downloadFile(
        job.annotated_output_url,
        `job_${job.job_number}_annotated.${isVideo ? "mp4" : "png"}`
      );
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="h-16 bg-slate-800/50 rounded-lg animate-pulse border border-slate-700/50"
          />
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <EmptyState
        icon={<FileSearch className="w-12 h-12" />}
        title="No jobs found"
        description="Start by creating your first analysis job"
        action={
          onRefresh
            ? {
                label: "Refresh",
                onClick: onRefresh,
              }
            : undefined
        }
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-700/50">
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4" />
                Job ID
              </div>
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Job Type
              </div>
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">
              <div className="flex items-center justify-center gap-2">
                <Clock className="w-4 h-4" />
                Submitted
              </div>
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">
              <div className="flex items-center justify-center gap-2">
                <Star className="w-4 h-4" />
                Status
              </div>
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">
              <div className="flex items-center justify-center gap-2">
                <Zap className="w-4 h-4" />
                Mode
              </div>
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">
              <div className="flex items-center justify-center gap-2">
                <Link2 className="w-4 h-4" />
                Actions
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const icon = JOB_TYPE_ICONS[job.job_type] || <BarChart3 className="w-4 h-4" />;
            return (
              <tr
                key={job.id}
                className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
              >
                {/* Job Number */}
                <td className="py-4 px-4">
                  <span className="text-white font-mono text-sm">
                    #{job.job_number}
                  </span>
                </td>
                
                {/* Job Type */}
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center text-primary-400">
                      {icon}
                    </div>
                    <span className="text-slate-300">
                      {formatJobType(job.job_type)}
                    </span>
                  </div>
                </td>
                
                {/* Submitted Date */}
                <td className="py-4 px-4 text-center">
                  <span className="text-slate-400 text-sm">
                    {formatDate(job.submitted_at, "MM/dd/yyyy")}
                  </span>
                </td>
                
                {/* Status */}
                <td className="py-4 px-4">
                  <div className="flex justify-center">
                    <Badge
                      variant={
                        job.status === JobStatus.COMPLETED
                          ? "success"
                          : job.status === JobStatus.FAILED
                          ? "error"
                          : job.status === JobStatus.PROCESSING
                          ? "info"
                          : "warning"
                      }
                    >
                      {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                    </Badge>
                  </div>
                </td>
                
                {/* Mode */}
                <td className="py-4 px-4">
                  <div className="flex justify-center">
                    {job.is_live_demo ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-accent-500/20 text-accent-400 border border-accent-500/30">
                        <Video className="w-3 h-3" />
                        Live Demo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-primary-500/20 text-primary-400 border border-primary-500/30">
                        <BarChart3 className="w-3 h-3" />
                        Standard
                      </span>
                    )}
                  </div>
                </td>

                {/* Actions */}
                <td className="py-4 px-4">
                  <div className="flex items-center justify-center gap-2">
                    {/* View - Subtle outline */}
                    <button
                      onClick={() => handleViewJob(job.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 transition-all"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </button>
                    
                    {/* Download - Subtle with muted accent */}
                    {job.status === JobStatus.COMPLETED && (
                      <button
                        onClick={() => handleDownload(job)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-300 hover:text-primary-300 bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 hover:border-primary-500/30 transition-all"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </button>
                    )}
                    
                    {/* Delete - Subtle with muted red on hover */}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(job.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-400 hover:text-red-400 bg-slate-700/50 hover:bg-red-500/10 border border-slate-600/50 hover:border-red-500/30 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
